/**
 * Logged in, but without a TransectLibre role (roles are given by the
 * TransectLibre administrators in Beheer › Gebruikers; licensed colleagues
 * normally start as Lezer). Rendered instead of the app shell, so nothing but
 * GET /api/me is requested for such a session (every other API route would
 * answer 403). No license, a guest account or revoked access never get this
 * far: the server shows its own page for those at login.
 */
import { LogIn, LogOut, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router';
import type { MeDto } from '../../shared/api.js';
import { Button, buttonClass } from '../components/Button.js';
import { InlineError } from '../components/States.js';
import { errorMessage } from '../lib/api.js';
import { loginUrl } from '../lib/navigation.js';
import { useDocumentTitle } from '../lib/prefs.js';
import { logout, recheckSession } from '../lib/session.js';
import styles from './Auth.module.css';
import { AuthLayout } from './AuthLayout.js';

export function NoAccessPage({ me }: { me: MeDto }) {
  useDocumentTitle('Geen toegang');
  const location = useLocation();
  const [busy, setBusy] = useState<'check' | 'logout' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // A role given meanwhile applies right away: the refreshed session replaces this page.
  const check = async () => {
    setBusy('check');
    setMessage(null);
    try {
      const fresh = await recheckSession();
      if (fresh && fresh.user.roles.length === 0) setMessage('Je hebt nog geen rol gekregen.');
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AuthLayout>
      <div className={styles.intro}>
        <h1 className={styles.title}>Geen toegang</h1>
        <p>Je bent ingelogd, maar je account heeft (nog) geen rol in TransectLibre. Daardoor kun je de planning niet bekijken.</p>
      </div>

      <p className={styles.account}>
        <strong>{me.user.name}</strong>
        <span className="small muted">{me.user.email}</span>
      </p>

      <div className="stack small">
        <p>
          Vraag een <strong>beheerder van TransectLibre</strong> om je een rol te geven: Lezer, Planner of Beheerder. Beheerders doen dat in TransectLibre zelf,
          onder Beheer › Gebruikers.
        </p>
        <p className="muted">Heb je de rol gekregen? Kies dan “Opnieuw controleren”; je hoeft niet opnieuw in te loggen.</p>
      </div>

      <InlineError message={message} />

      <div className={styles.actions}>
        <Button variant="primary" size="lg" icon={<RefreshCw />} busy={busy === 'check'} disabled={busy !== null} onClick={() => void check()}>
          Opnieuw controleren
        </Button>
        <a href={loginUrl(`${location.pathname}${location.search}`, { selectAccount: true })} className={buttonClass('default', 'lg')}>
          <LogIn aria-hidden="true" />
          Ander account
        </a>
        <Button
          size="lg"
          variant="ghost"
          icon={<LogOut />}
          busy={busy === 'logout'}
          disabled={busy !== null}
          onClick={() => {
            setBusy('logout');
            void logout();
          }}
        >
          Uitloggen
        </Button>
      </div>
    </AuthLayout>
  );
}
